import { Controller, UseFormReturn, useWatch } from 'react-hook-form';

import { Label } from 'components/Label';
import { MultiSelect } from 'components/MultiSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Flex, Grid } from 'components/layout';

import { LTLSelectedAccessorial, QuickQuoteInputs } from '../../types';
import { AccessorialCard } from '../AccessorialCard';
import {
  TURVO_DELIVERY_SERVICE_DEFAULT,
  TURVO_DELIVERY_SERVICE_OPTIONS,
  TURVO_PAYMENT_TERM_DEFAULT,
  TURVO_PAYMENT_TERM_OPTIONS,
  TURVO_PICKUP_SERVICE_DEFAULT,
  TURVO_PICKUP_SERVICE_OPTIONS,
  TURVO_SERVICE_TYPE_DEFAULT,
  TURVO_SERVICE_TYPE_OPTIONS,
} from './constants';

interface ServiceAndPaymentSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
}

/** Turvo LTL: Service type and Payment terms, rendered below Items section. */
export function ServiceAndPaymentSection({
  formMethods,
}: ServiceAndPaymentSectionProps) {
  const { control, setValue } = formMethods;
  const pickupServices =
    useWatch({
      control,
      name: 'ltl.pickupServices',
      defaultValue: TURVO_PICKUP_SERVICE_DEFAULT,
    }) ?? TURVO_PICKUP_SERVICE_DEFAULT;
  const hasSelectedPickupServices = pickupServices.length > 0;
  const deliveryServices =
    useWatch({
      control,
      name: 'ltl.deliveryServices',
      defaultValue: TURVO_DELIVERY_SERVICE_DEFAULT,
    }) ?? TURVO_DELIVERY_SERVICE_DEFAULT;
  const hasSelectedDeliveryServices = deliveryServices.length > 0;

  function updatePickupServices(nextValues: string[]) {
    setValue('ltl.pickupServices', nextValues, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  function removePickupService(serviceToRemove: string) {
    updatePickupServices(
      pickupServices.filter((service) => service !== serviceToRemove)
    );
  }

  function updateDeliveryServices(nextValues: string[]) {
    setValue('ltl.deliveryServices', nextValues, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  function removeDeliveryService(serviceToRemove: string) {
    updateDeliveryServices(
      deliveryServices.filter((service) => service !== serviceToRemove)
    );
  }

  return (
    <Flex direction='col' gap='lg' className='w-full'>
      <Grid cols='2' gap='xs' align='start' className='w-full mx-0'>
        <div className='w-full min-w-0'>
          <Label
            name='ltl.serviceType'
            className='text-[13px] font-medium'
            hideAIHint
          >
            Service type
          </Label>
          <Controller
            name='ltl.serviceType'
            control={control}
            defaultValue={TURVO_SERVICE_TYPE_DEFAULT}
            render={({ field }) => (
              <Select
                onValueChange={field.onChange}
                value={field.value ?? TURVO_SERVICE_TYPE_DEFAULT}
              >
                <SelectTrigger className='w-full mt-1'>
                  <SelectValue placeholder='Service type' />
                </SelectTrigger>
                <SelectContent>
                  {TURVO_SERVICE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
        <div className='w-full min-w-0'>
          <Label
            name='ltl.paymentTerm'
            className='text-[13px] font-medium'
            hideAIHint
          >
            Payment terms
          </Label>
          <Controller
            name='ltl.paymentTerm'
            control={control}
            defaultValue={TURVO_PAYMENT_TERM_DEFAULT}
            render={({ field }) => {
              const raw = field.value ?? TURVO_PAYMENT_TERM_DEFAULT;
              const displayValue =
                raw === '' ? TURVO_PAYMENT_TERM_DEFAULT : raw;
              return (
                <Select onValueChange={field.onChange} value={displayValue}>
                  <SelectTrigger className='w-full mt-1'>
                    <SelectValue placeholder='Payment terms' />
                  </SelectTrigger>
                  <SelectContent>
                    {TURVO_PAYMENT_TERM_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }}
          />
        </div>
      </Grid>
      <div className='w-full min-w-0'>
        <Label
          name='ltl.pickupServices'
          className='text-[13px] font-medium'
          hideAIHint
        >
          Pickup services
        </Label>
        <MultiSelect
          placeholder='Select pickup services...'
          value={pickupServices}
          onChange={(nextValues) => updatePickupServices(nextValues)}
          options={TURVO_PICKUP_SERVICE_OPTIONS}
          className='w-full mt-1'
          displayMode='count'
        />
        {hasSelectedPickupServices && (
          <Flex direction='col' className='w-full mt-2'>
            {pickupServices.map((service, index) => {
              const selectedPickupService: LTLSelectedAccessorial = {
                code: service,
                name: service,
                externalId: 0,
              };

              return (
                <AccessorialCard
                  key={`${service}-${index}`}
                  index={index}
                  selected={selectedPickupService}
                  control={control}
                  onRemove={() => removePickupService(service)}
                />
              );
            })}
          </Flex>
        )}
      </div>
      <div className='w-full min-w-0'>
        <Label
          name='ltl.deliveryServices'
          className='text-[13px] font-medium'
          hideAIHint
        >
          Delivery services
        </Label>
        <MultiSelect
          placeholder='Select delivery services...'
          value={deliveryServices}
          onChange={(nextValues) => updateDeliveryServices(nextValues)}
          options={TURVO_DELIVERY_SERVICE_OPTIONS}
          className='w-full mt-1'
          displayMode='count'
        />
        {hasSelectedDeliveryServices && (
          <Flex direction='col' className='w-full mt-2'>
            {deliveryServices.map((service, index) => {
              const selectedDeliveryService: LTLSelectedAccessorial = {
                code: service,
                name: service,
                externalId: 0,
              };

              return (
                <AccessorialCard
                  key={`${service}-${index}`}
                  index={index}
                  selected={selectedDeliveryService}
                  control={control}
                  onRemove={() => removeDeliveryService(service)}
                />
              );
            })}
          </Flex>
        )}
      </div>
    </Flex>
  );
}
